import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClientsService } from "../services/clients";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import { useNavigate } from "react-router";
import {
  ROLE_CLIENT_ADMIN,
  ROLE_SUPER_ADMIN,
  ROUTE_CLIENTS,
  ROUTE_FORBIDDEN_PAGE,
} from "../../SharedModule/utils/constants";
import {
  IsClientsLoaded,
  IsClientsLoading,
  SelectActualClientForHeaderInfo,
  SelectClientsIds,
  SelectOrderedBy,
  setActualClientForHeaderInfo,
  setIsActualClientForHeaderInfoLoading,
} from "../redux/reducers/clientsList.reducer";
import {
  IsApplySupervisorCheck,
  IsLoadedAllCostCenters,
  IsLoadingAllCostCenters,
  IsLoadingReviews,
  IsReviewsLoaded,
  IsShowingActivesInTab,
  loadAllCostCenters,
  loadCostCentersOwned,
  loadReviewsInfo,
  SelectAllCostCenter,
  SelectCostCenterOwnedIds,
  SelectOrderedByReviewsList,
  setIsLoadingAllCostCenters,
  setIsLoadingReviews,
} from "../redux/reducers/clientRelationshipReviewsList.reducer";
import { UsersService } from "../../UsersModule/services/users";
import { Client } from "../types/clients-module-types";
import {
  isSupervisor,
  verifyModuleAccess,
} from "../../SharedModule/utils/accessUtils";
import { SelectCostCentersOwned } from "../../UsersModule/redux/reducers/usersList.reducer";

import { useClientsList } from "./useClientsList";

const loadCostCentersIfSupervisor = async (
  inFlightRef,
  isSupervisorInfo,
  notSuperOrClientAdmin,
  costCentersOwned,
  activeUserEmail,
  dispatch
) => {
  if (
    inFlightRef.current.supervisor ||
    !isSupervisorInfo ||
    !notSuperOrClientAdmin ||
    costCentersOwned
  ) {
    return;
  }
  inFlightRef.current.supervisor = true;
  let costCentersOwnedResponse;
  if (!costCentersOwned) {
    costCentersOwnedResponse = await UsersService.getCostCentersOwned(
      activeUserEmail
    );
  }
  inFlightRef.current.supervisor = false;
  dispatch(
    loadCostCentersOwned({
      costCentersOwned: costCentersOwned || costCentersOwnedResponse,
      applySupervisorCheck: true,
    })
  );
};

const loadHeaderInfoIfNeeded = async (
  inFlightRef,
  headerDoneRef,
  clientId,
  clientForHeaderInfo,
  dispatch,
  navigate
) => {
  if (
    inFlightRef.current.header ||
    headerDoneRef.current === clientId ||
    (clientForHeaderInfo && clientForHeaderInfo.id === clientId)
  ) {
    return;
  }
  inFlightRef.current.header = true;
  dispatch(setIsActualClientForHeaderInfoLoading(true));
  const headerInfo = await ClientsService.getClientModuleHeaderInfo(clientId)
    .then((response) => {
      if (!response.success) {
        dispatch(
          addToast({
            mode: "error",
            message: response.message,
          })
        );
        dispatch(setIsActualClientForHeaderInfoLoading(false));
        navigate(ROUTE_CLIENTS);
        return true;
      }
      return response.data;
    })
    .catch(() => {
      dispatch(
        addToast({
          mode: "error",
          message:
            "Something wrong happened while getting the client header information.",
        })
      );
      dispatch(setIsActualClientForHeaderInfoLoading(false));
      navigate(ROUTE_CLIENTS);
      return true;
    });
  inFlightRef.current.header = false;
  headerDoneRef.current = clientId;
  dispatch(setActualClientForHeaderInfo(headerInfo));
};

const loadAllCostCentersIfNeeded = async (
  inFlightRef,
  isLoadedAllCostCenters,
  dispatch
) => {
  if (inFlightRef.current.costCenters || isLoadedAllCostCenters) {
    return;
  }
  inFlightRef.current.costCenters = true;
  dispatch(setIsLoadingAllCostCenters(true));
  const allCostCenters = await UsersService.getCostCenters();
  inFlightRef.current.costCenters = false;
  dispatch(loadAllCostCenters(allCostCenters));
};

export const useClientRelationshipReviewTab = (clientId: number) => {
  useClientsList();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userRolesList, rolesLoaded, activeUserEmail } =
    useSelector(selectUserRoles);

  const reviewsLoaded = useSelector(IsReviewsLoaded);
  const isApplySupervisorCheck = useSelector(IsApplySupervisorCheck);
  const costCenterOwnedIds = useSelector(SelectCostCenterOwnedIds);
  const isLoadingReviews = useSelector(IsLoadingReviews);
  const { reviewsList: clientRelationshipReview } = useSelector(
    SelectOrderedByReviewsList
  );

  const costCentersOwned = useSelector(SelectCostCentersOwned);
  const isSupervisorInfo = isSupervisor(userRolesList);
  const notSuperOrClientAdmin = !verifyModuleAccess(
    true,
    [ROLE_SUPER_ADMIN, ROLE_CLIENT_ADMIN],
    userRolesList
  );

  const isClientsLoading = useSelector(IsClientsLoading);
  const { clientlist: clients } = useSelector(SelectOrderedBy);
  const allCostCenters = useSelector(SelectAllCostCenter);
  const isLoadedAllCostCenters = useSelector(IsLoadedAllCostCenters);
  const isLoadingAllCostCenters = useSelector(IsLoadingAllCostCenters);

  const isShowingActivesInTab = useSelector(IsShowingActivesInTab);

  // To check is header info is already loaded
  const ClientForHeaderInfo: Client = useSelector(
    SelectActualClientForHeaderInfo
  );
  // to check if clientId is in list of clients that user could see
  const isClientsLoaded = useSelector(IsClientsLoaded);
  const clientsIds = useSelector(SelectClientsIds);

  // Tracks which fetch blocks are in flight, and (in doneRef) which clientId
  // each per-client block last completed for. Both callbacks are async and
  // not awaited by the effect, so a block's dispatch can change a dependency
  // and re-run the effect (and both callbacks) while an earlier request is
  // still pending. A second, overlapping invocation is a *different*
  // closure — its own `ClientForHeaderInfo`/`isLoadedAllCostCenters`/
  // `reviewsLoaded` reads are frozen at whatever they were when THAT closure
  // was created, so an in-flight-only guard isn't enough. doneRef is a plain
  // mutable ref, so every invocation (stale or not) reads its truly-current
  // value.
  const inFlightRef = useRef<Record<string, boolean>>({});
  const headerDoneRef = useRef<number | undefined>(undefined);

  const loadRelationshipReviewInfoTab = useCallback(
    async (clientId) => {
      if (!rolesLoaded || !isClientsLoaded) {
        return;
      }
      // This check should go in all clients custom hooks
      if (!clientsIds.includes(clientId)) {
        dispatch(
          addToast({
            mode: "error",
            message: "You're not allowed to see this Client Page",
          })
        );
        navigate(ROUTE_FORBIDDEN_PAGE);
        return true;
      }
      // Load info when user is supervisor
      await loadCostCentersIfSupervisor(
        inFlightRef,
        isSupervisorInfo,
        notSuperOrClientAdmin,
        costCentersOwned,
        activeUserEmail,
        dispatch
      );
      // Load info header when user put a new URL
      await loadHeaderInfoIfNeeded(
        inFlightRef,
        headerDoneRef,
        clientId,
        ClientForHeaderInfo,
        dispatch,
        navigate
      );
      // load cost centers isLoadedAllCostCenters
      await loadAllCostCentersIfNeeded(
        inFlightRef,
        isLoadedAllCostCenters,
        dispatch
      );
    },
    [dispatch, rolesLoaded, isClientsLoaded, ClientForHeaderInfo, isLoadedAllCostCenters, isSupervisorInfo, costCentersOwned]
  );

  const loadRelationshipReview = useCallback(
    async (needReviewRefresh) => {
      if (
        !inFlightRef.current.reviews &&
        rolesLoaded &&
        (!reviewsLoaded || (reviewsLoaded && needReviewRefresh)) &&
        isClientsLoaded &&
        ClientForHeaderInfo &&
        clientsIds.includes(clientId)
      ) {
        inFlightRef.current.reviews = true;
        dispatch(setIsLoadingReviews(true));
        const reviewsInfo = await ClientsService.getClientRelationshipReviews();
        inFlightRef.current.reviews = false;
        dispatch(loadReviewsInfo({ data: reviewsInfo.data, clients: clients }));
      }
    },
    [dispatch, rolesLoaded, isClientsLoaded, ClientForHeaderInfo, reviewsLoaded]
  );

  useEffect(() => {
    loadRelationshipReviewInfoTab(clientId);
    loadRelationshipReview(false);
  }, [loadRelationshipReviewInfoTab]);

  return {
    ClientForHeaderInfo,
    userRolesList,
    isLoadingReviews,
    isApplySupervisorCheck,
    costCenterOwnedIds,
    rolesLoaded,
    clientRelationshipReview,
    isShowingActivesInTab,
    loadRelationshipReview,
    setIsLoadingReviews,
    isClientsLoading,
    clients,
    isLoadingAllCostCenters,
    allCostCenters,
  };
};
